import { object, string, StringSchema } from 'yup'
import { useEffect, useMemo, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { convertObjectNullValuesToEmptyString } from 'shared/utils'
import {
  useDebouncedCallback,
  useValidationRules,
  usePrevious,
} from 'shared/hooks'
import {
  LoadingStatus,
  TaxNumberErrorCode,
  TaxNumberErrorKey,
} from 'shared/types'
import actions from 'shared/store/contractors/actions'
import { selectors } from 'shared/store'
import { FormValues } from './ContractorForm.types'

type ErrorCodeTranslationMapping = Record<TaxNumberErrorCode, TaxNumberErrorKey>

export const errorKeysMapping: ErrorCodeTranslationMapping = {
  [TaxNumberErrorCode.alreadyAdded]: TaxNumberErrorKey.alreadyAdded,
  [TaxNumberErrorCode.doesNotExist]: TaxNumberErrorKey.doesNotExist,
}

export const useGetTaxNumberState = () => {
  const { loading, error, data } = useSelector(
    selectors.contractors.getContractorDialogTaxNumberFieldState
  )
  const isTaxFieldLoading = loading === LoadingStatus.Pending
  return {
    isTaxFieldLoading,
    taxNumberError: error as TaxNumberErrorCode,
    data,
  }
}

export const useAutofill = (
  initialValues: FormValues,
  setInitialValues: (data: FormValues) => void,
  countryId: number
) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const {
    basicValidation: { taxNumber },
  } = useValidationRules()
  const [inputValue, setInputValue] = useState('')
  const { data, isTaxFieldLoading } = useGetTaxNumberState()
  const previousInputValue = usePrevious(inputValue)

  const fetchContractor = useCallback(async () => {
    if (
      (!previousInputValue && !inputValue) ||
      (previousInputValue && !inputValue)
    )
      return
    await dispatch(actions.clearTaxNumberError())
    dispatch(
      actions.fetchContractorByTaxNumber({
        countryId,
        taxNumber: inputValue,
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue])

  useEffect(() => {
    if (data) {
      const { countryId } = initialValues
      setInitialValues({
        ...initialValues,
        ...convertObjectNullValuesToEmptyString(data || {}),
        countryId,
      })
    }
    // eslint-disable-next-line
  }, [isTaxFieldLoading])

  const defaultValidation = string().required(t('errors:fillThisField'))

  const taxNumberValidation: {
    [key: string]: any
  } = {
    PL: taxNumber,
  }
  const handleAutofill = async (value: string, countryCode?: string) => {
    if (!countryCode) return
    const validationSchema =
      taxNumberValidation[countryCode] || defaultValidation
    try {
      await validationSchema.validate(value)
      setInputValue(value)
    } catch (e) {}
  }

  useDebouncedCallback(fetchContractor, 500)

  return { handleAutofill }
}

export const useValidationSchema = () => {
  const { t } = useTranslation()
  const { error, loading } = useSelector(
    selectors.contractors.getContractorDialogTaxNumberFieldState
  )
  const { getObjectShape } = useValidationRules()

  const shape = getObjectShape([
    {
      required: true,
      name: 'buildingNumber',
      rule: 'buildingNumber',
    },
    {
      name: 'apartmentNumber',
      rule: 'apartmentNumber',
    },
    {
      name: 'emailAddress',
      rule: 'email',
    },
    {
      name: 'phoneNumber',
      rule: 'phoneNumber',
    },
    {
      required: true,
      name: 'city',
      rule: 'city',
    },
    {
      required: true,
      name: 'zipCode',
      rule: 'postalCode',
    },
    {
      required: true,
      name: 'street',
      rule: 'street',
    },
    {
      name: 'nationalBusinessRegistryNumber',
      rule: 'businessRegistryNumber',
    },
    {
      required: true,
      name: 'name',
      rule: 'contractorName',
    },
    {
      required: true,
      name: 'taxNumber',
      rule: 'taxNumber',
    },
  ])

  useEffect(() => {
    console.log(error)
  }, [error])

  const schemas: Record<string, any> = useMemo(
    () => ({
      PL: object().shape({
        ...shape,
        taxNumber: (shape.taxNumber as StringSchema).test(
          'tax-number-error',
          t(
            error
              ? `errors:taxNumber.${
                  errorKeysMapping[error as TaxNumberErrorCode]
                }`
              : ''
          ),
          () => {
            return !error
          }
        ),
      }),
      default: object().shape({
        ...shape,
        taxNumber: string()
          .required(t('errors:fillThisField'))
          .test(
            'tax-number-error',
            t(
              error === TaxNumberErrorCode.alreadyAdded
                ? `errors:taxNumber.${errorKeysMapping[error]}`
                : ''
            ),
            () => {
              return !(error === TaxNumberErrorCode.alreadyAdded)
            }
          )
          .max(20, t('errors:taxNumber.length', { count: 20 })),
        nationalBusinessRegistryNumber: string(),
      }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [error, shape, t, loading]
  )
  return schemas
}
